import type { HistoryListItem } from "@/api/history/Types";
import { useChatContext } from "@/components/chatview/ChatContext";
import { useChatIdStore } from "@/store/ChatIdStore";
import { useAgentStore } from "@/store/AgentStore";
import { useMemo } from "react";

export type ChatHistoryItem = HistoryListItem;

interface HistoryListProps {
  historyData: ChatHistoryItem[];
  onLoadHistory?: (sessionId: number) => void;
  onClose: () => void;
  selectedChat: number | null;
  onSelectChat: (id: number | null) => void;
}

interface GroupedSessions {
  agent: {
    id: string;
    name: string;
    icon: string;
    iconColor: string;
    iconBgColor: string;
    isDefault: boolean;
  };
  sessions: ChatHistoryItem[];
}

export function HistoryList({
  historyData,
  onLoadHistory,
  onClose,
  selectedChat,
  onSelectChat,
}: HistoryListProps) {
  const { setActiveQuote } = useChatContext();
  const { setStartChatId } = useChatIdStore();
  const { getSessionAgent, getDefaultAgent } = useAgentStore();

  const groupedData = useMemo(() => {
    const groups: Record<string, GroupedSessions> = {};
    const defaultAgent = getDefaultAgent();

    historyData.forEach((session) => {
      const agent = getSessionAgent(String(session.id)) ?? defaultAgent;

      if (!groups[agent.id]) {
        groups[agent.id] = {
          agent: {
            id: agent.id,
            name: agent.name,
            icon: agent.icon,
            iconColor: agent.iconColor,
            iconBgColor: agent.iconBgColor,
            isDefault: agent.isDefault,
          },
          sessions: [],
        };
      }

      groups[agent.id].sessions.push(session);
    });

    return Object.values(groups).sort((a, b) => {
      if (a.agent.isDefault && !b.agent.isDefault) return -1;
      if (!a.agent.isDefault && b.agent.isDefault) return 1;
      return a.agent.name.localeCompare(b.agent.name, "zh-CN");
    });
  }, [historyData, getSessionAgent, getDefaultAgent]);

  const handleChatSelect = (sessionId: number) => {
    onSelectChat(sessionId);
    setActiveQuote?.(null);
    setStartChatId(0);

    if (onLoadHistory) {
      onLoadHistory(sessionId);
      onClose();
    }
  };

  if (historyData.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">暂无聊天历史</div>
    );
  }

  return (
    <div className="px-2">
      {groupedData.map((group) => (
        <div key={group.agent.id} className="mb-3">
          <div className="mt-1 pl-2">
            {group.sessions.map((item) => {
              const isSelected = selectedChat === item.id;
              return (
                <div
                  key={item.id}
                  className="pr-2 py-1 cursor-pointer group"
                  onClick={() => handleChatSelect(item.id)}
                >
                  <div
                    className={`py-1.75 px-3 rounded-xl transition-colors ${
                      isSelected
                        ? "bg-blue-50 text-blue-600"
                        : "bg-[#F5F5F5] text-[#0F0F0F] group-hover:bg-blue-50 group-hover:text-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <div
                        className={`text-sm font-normal truncate ${
                          isSelected ? "text-blue-600" : ""
                        }`}
                      >
                        {item.title}
                      </div>
                    </div>
                    <div
                      className={`text-xs ${
                        isSelected ? "text-blue-400" : "text-[#0F0F0F]"
                      }`}
                    >
                      {new Date(item.create_time).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
